// The deterministic rules behind the Contacts tab and the Connect board. No API calls and no
// Vue here, so each rule can be read (and tested) on its own. Every rule works on fields the API
// returns; nothing is inferred.

// The pipeline columns a new user starts with: the three CGS2022 command center lists
// (Prequalifications, Waiting for Docs, In Contract). A user can pick any of the CGS statuses
// from Options; the choice is kept per browser.
export const DEFAULT_PIPELINE_STATUSES = ['Pre-Approved', 'Waiting on Docs', 'In Contract']
export const PIPELINE_COLUMNS_KEY = 'connect.pipelineStatuses'

// Days since the contact's own "updated" stamp, rounded down. null when there's no usable date,
// so a missing date never shows as "fresh".
export function daysSince(value, now = Date.now()) {
  if (!value) return null
  const t = new Date(value).getTime()
  if (isNaN(t)) return null
  return Math.max(0, Math.floor((now - t) / 86400000))
}

// Staleness bands: under 7 days fresh, under 30 aging, 30+ stale. Unknown stays neutral.
export function staleBand(days) {
  if (days === null || days === undefined) return 'none'
  if (days < 7) return 'fresh'
  if (days < 30) return 'aging'
  return 'stale'
}

export function staleLabel(days) {
  if (days === null || days === undefined) return 'No update date'
  if (days === 0) return 'Updated today'
  return `${days}d since update`
}

// Column colours by position, from the command center palette (commandcenter.css --cc-*).
const COLUMN_COLORS = ['#14b8a6', '#f59e0b', '#6366f1', '#3b82f6', '#22c55e', '#ef4444', '#64748b']
export function columnColor(index) {
  return COLUMN_COLORS[index % COLUMN_COLORS.length]
}

export function initials(name) {
  return String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export function displayName(c) {
  if (!c) return ''
  return c.display_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || 'Unnamed contact'
}

// Contact type pill colours. Same groupings as GSDesign's TYPE_CLASSES, in swiftApp colours.
const TYPE_CLASSES = {
  client: 'cx-pill--blue',
  lead: 'cx-pill--amber',
  prospect: 'cx-pill--amber',
  agent: 'cx-pill--teal',
  realtor: 'cx-pill--teal',
  'title agent': 'cx-pill--teal',
  recruit: 'cx-pill--violet',
  'financial advisor': 'cx-pill--violet',
  'insurance advisor': 'cx-pill--violet',
  staff: 'cx-pill--gray',
  'vendor contact': 'cx-pill--gray'
}
export function typeClass(type) {
  return TYPE_CLASSES[String(type || '').toLowerCase()] || 'cx-pill--gray'
}

export function formatPhone(v) {
  const d = String(v || '').replace(/\D/g, '')
  const ten = d.length === 11 && d[0] === '1' ? d.slice(1) : d
  if (ten.length !== 10) return v || ''
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
}

// 'YYYY-MM-DD…' is a calendar date: built locally so it doesn't shift a day across time zones.
export function fmtDate(value, opts = { month: 'short', day: 'numeric', year: 'numeric' }) {
  if (!value) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value))
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value)
  if (isNaN(d)) return String(value)
  return d.toLocaleDateString(undefined, opts)
}

export function fmtWhen(value, now = Date.now()) {
  const x = value ? new Date(value) : null
  if (!x || isNaN(x)) return ''
  const days = Math.round((now - x.getTime()) / 86400000)
  if (days === 0) return x.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  if (days === 1) return 'Yesterday'
  return fmtDate(x, days > 300 ? undefined : { month: 'short', day: 'numeric' })
}

export function money(v) {
  const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''))
  if (!v || isNaN(n) || n === 0) return ''
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

export function rate(v) {
  const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''))
  if (!v || isNaN(n) || n === 0) return ''
  return `${n.toFixed(3)}%`
}

// The timeline filter. "Campaigns" is left out: campaign entries are marketing and are hidden.
export const TIMELINE_KINDS = [
  { id: 'all', label: 'All' },
  { id: 'call', label: 'Calls' },
  { id: 'message', label: 'Texts and emails' },
  { id: 'note', label: 'Notes' },
  { id: 'change', label: 'Changes' }
]
export function timelineMatches(item, filter) {
  if (item.kind === 'campaign') return false
  if (filter === 'all') return true
  if (filter === 'message') return item.kind === 'text' || item.kind === 'email'
  return item.kind === filter
}

// The pipeline columns the user picked, in the order they added them, kept per browser and per
// list: a saved list ("saved:<title>") has its own columns, everything else shares "default".
// CGS saved lists only store a title and filters (GridFilter), so the columns can't live on the
// server without an API change. A list with no columns of its own starts from "default", then
// from the CGS2022 command center three. Only statuses the server still offers are kept, so a
// renamed status can't leave an empty column.
function columnsKey(listKey) {
  return listKey && listKey !== 'default' ? `${PIPELINE_COLUMNS_KEY}:${listKey}` : PIPELINE_COLUMNS_KEY
}
function readColumns(key) {
  try {
    const v = JSON.parse(window.localStorage.getItem(key) || 'null')
    return Array.isArray(v) && v.length ? v : null
  } catch (e) { return null }
}
export function loadPipelineStatuses(available, listKey = 'default') {
  const pick = readColumns(columnsKey(listKey)) || readColumns(columnsKey('default')) || DEFAULT_PIPELINE_STATUSES
  if (!Array.isArray(available) || !available.length) return pick.slice()
  const kept = pick.filter(s => available.includes(s))
  return kept.length ? kept : DEFAULT_PIPELINE_STATUSES.filter(s => available.includes(s))
}
export function savePipelineStatuses(list, listKey = 'default') {
  try { window.localStorage.setItem(columnsKey(listKey), JSON.stringify(list)) } catch (e) {}
}
// Carry a saved list's columns over when it's renamed, and drop them when it's removed.
export function movePipelineStatuses(fromKey, toKey) {
  try {
    const v = window.localStorage.getItem(columnsKey(fromKey))
    if (v !== null && toKey) window.localStorage.setItem(columnsKey(toKey), v)
    window.localStorage.removeItem(columnsKey(fromKey))
  } catch (e) {}
}

// Two filter lists are the same when they hold the same field / op / value, in any order.
export function sameFilters(a, b) {
  const norm = list => (list || []).map(f => `${f.field}|${f.op}|${String(f.value ?? '').trim()}`).sort().join('\n')
  return norm(a) === norm(b)
}

// swiftApp's own loan page (the same ConsumerPortal link the pipeline grids open).
export function swiftLoanUrl(loanId) {
  if (!loanId) return ''
  const base = String(process.env.VUE_APP_SWIFT_URL || 'https://swift.goldstarfinancial.com/').replace(/\/?$/, '/')
  return `${base}ConsumerPortal?id=${encodeURIComponent(loanId)}&swiftprocessing=true`
}

// CGS stores birthdays as "MM/DD/YYYY", sometimes with a time, and uses 01/01/0001 for "none".
// Shown as "Mar 4, 1985" (or "Mar 4" when the year is missing or a placeholder); '' for none.
export function birthdayLabel(value) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{1,4})/.exec(String(value || '').trim())
  if (!m) return ''
  const month = Number(m[1]); const day = Number(m[2]); const year = Number(m[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return ''
  if (year <= 1 && month === 1 && day === 1) return '' // 01/01/0001: CGS's empty date
  const realYear = year >= 1900
  const d = new Date(realYear ? year : 2000, month - 1, day)
  return d.toLocaleDateString(undefined, realYear ? { month: 'short', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric' })
}

// Status pill colours, GSDesign-style: soft, and grouped by where the contact is.
export function statusClass(status) {
  const s = String(status || '').toLowerCase()
  if (/hot|warm/.test(s)) return 'cx-pill--amber'
  if (/pre-?approved|prequal|in contract|converted|^active$/.test(s)) return 'cx-pill--teal'
  if (/waiting|app\. link|follow/.test(s)) return 'cx-pill--blue'
  return 'cx-pill--gray'
}
